import jsQR from 'jsqr'

/**
 * Læsningen af koder fra kameraet - både husets egne QR-mærkater og
 * leverandørernes stregkoder (docs/DECISIONS.md beslutning 069).
 *
 * <b>Tre veje, i den rækkefølge de koster noget:</b>
 *
 * 1. `BarcodeDetector` er indbygget i Chrome og Android. Den er hurtigst, mest
 *    strømbesparende og kan begge slags koder. Findes den, bruges kun den.
 * 2. `jsQR` er reserven til QR. Den er lille og ligger allerede i bundtet.
 * 3. ZXing er reserven til stregkoder - dét er den, iPhone har brug for.
 *    **Den hentes først, når den skal bruges**: den fylder mere end resten af
 *    scanneren tilsammen, og de fleste åbner aldrig en scanner.
 *
 * Uden punkt 3 kunne en iPhone kun læse QR, og så ville en æske fra grossisten
 * skulle mærkes op i hånden, før den kunne scannes - hvilket er præcis dét,
 * stregkoderne findes for at undgå.
 */

/** Koder scanneren kigger efter. Rækkefølgen betyder ingenting. */
const FORMATS = ['qr_code', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39']

export interface ScannedCode {
  /** Selve teksten - en URL, en kode eller en række cifre. */
  text: string
  /** Hvad slags kode det var, hvis læseren kunne sige det. */
  format?: string
}

/** Kan browseren læse stregkoder selv, eller skal ZXing hentes? */
export function hasNativeDetector(): boolean {
  return typeof window !== 'undefined' && 'BarcodeDetector' in window
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyDetector = any

let nativeDetector: AnyDetector | null = null
let zxingReader: AnyDetector | null = null
let zxingFailed = false

/**
 * Læser en kode fra et billede. Null betyder "der var ingen kode i det her
 * billede" - ikke at noget gik galt; kalderen prøver bare næste billede.
 *
 * @param wantBarcodes Skal der også kigges efter stregkoder? Er svaret nej,
 * hentes ZXing aldrig, og en enhedsscanner koster derfor ikke det ekstra.
 */
export async function readCode(
  canvas: HTMLCanvasElement,
  wantBarcodes: boolean
): Promise<ScannedCode | null> {
  // 1. Browserens egen - kan begge dele.
  if (hasNativeDetector()) {
    try {
      nativeDetector ??= new (window as AnyDetector).BarcodeDetector({
        formats: wantBarcodes ? FORMATS : ['qr_code']
      })

      const found = await nativeDetector.detect(canvas)
      if (found.length > 0) {
        return { text: found[0].rawValue, format: found[0].format }
      }

      // Browseren kunne laese billedet og fandt ingenting. Saa er der ingen
      // grund til at koere de langsomme reserver paa det samme billede.
      return null
    } catch {
      // Falder igennem. Nogle browsere melder BarcodeDetector, men fejler paa
      // formater, de alligevel ikke understoetter.
    }
  }

  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) return null

  const image = context.getImageData(0, 0, canvas.width, canvas.height)

  // 2. QR uden ekstra hentning.
  const qr = jsQR(image.data, image.width, image.height)
  if (qr?.data) {
    return { text: qr.data, format: 'qr_code' }
  }

  // 3. Stregkoder - kun hvis der faktisk skal laeses stregkoder.
  if (!wantBarcodes || zxingFailed) {
    return null
  }

  try {
    zxingReader ??= await loadZxingAsync()
    const result = zxingReader.decodeFromCanvas(canvas)
    const text = result?.getText?.()

    return text ? { text, format: result.getBarcodeFormat?.()?.toString() } : null
  } catch (error) {
    // ZXing kaster, naar der ikke er nogen kode i billedet - det er det normale
    // og maa ikke slukke for laeseren. Kun hvis selve biblioteket ikke kunne
    // hentes, giver vi op og lader vaere med at proeve igen.
    if (error instanceof Error && error.message.includes('Failed to fetch')) {
      zxingFailed = true
    }

    return null
  }
}

/**
 * Henter ZXing første gang, der skal læses en stregkode uden browserens egen
 * læser. Dynamisk import, så koden lander i sit eget bundt og ikke i det, alle
 * henter ved login.
 */
async function loadZxingAsync(): Promise<AnyDetector> {
  const { BrowserMultiFormatReader } = await import('@zxing/browser')
  return new BrowserMultiFormatReader()
}

/**
 * Gør det scannede klar til at blive slået op.
 *
 * <b>En QR fra vores egne mærkater indeholder en hel URL</b> (".../v/K7M2QX9P"),
 * og den kan ikke sendes som ét led i API-stien - kodede skråstreger afvises af
 * routingen. Koden pilles derfor ud her, før opslaget. Serveren gør det samme som
 * sikkerhedsnet (`ShortCode.Normalize`), så de to sider ikke kan komme til at
 * være uenige.
 *
 * <b>En stregkode røres ikke.</b> Den er bare cifre og har hverken skråstreger
 * eller store bogstaver at rette - og at "normalisere" den kunne ændre den.
 */
export function normalizeScanned(value: string): string {
  const text = value.trim()

  // Kun tal: det er en stregkode fra emballagen. Lad den være.
  if (/^[\d\s-]+$/.test(text)) {
    return text
  }

  let code = text

  const lastSlash = code.lastIndexOf('/')
  if (lastSlash >= 0 && lastSlash < code.length - 1) {
    code = code.slice(lastSlash + 1)
  }

  const query = code.indexOf('?')
  if (query >= 0) {
    code = code.slice(0, query)
  }

  return code.replace(/[\s-]/g, '').toUpperCase()
}

/**
 * Er det her en stregkode fra en leverandør? Bruges til at vise, hvad systemet
 * læste - ikke til at afgøre noget: opslaget prøver alligevel begge veje.
 */
export function looksLikeBarcode(text: string): boolean {
  const digits = text.replace(/\D/g, '')
  return digits.length >= 8 && digits.length === text.trim().length
}

/** Slukker kameraet og frigiver strømmen. */
export function stopCamera(video: HTMLVideoElement | null) {
  const stream = video?.srcObject as MediaStream | null
  stream?.getTracks().forEach((track) => track.stop())
  if (video) video.srcObject = null
}

/**
 * Tænder bagkameraet. Kaster med en besked, der kan vises direkte for brugeren -
 * mærkatet sidder på varen, ikke på manden.
 */
export async function startCamera(video: HTMLVideoElement): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('Denne browser kan ikke bruge kameraet. Tast koden i stedet.')
  }

  let stream: MediaStream
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: 'environment' } }
    })
  } catch {
    throw new Error(
      'Der er ikke adgang til kameraet. Giv appen lov i browserens indstillinger, eller tast koden.'
    )
  }

  video.srcObject = stream
  await video.play()

  return stream
}
